/**
 * Market profile and TPO.
 *
 * ## Everything numeric arrives computed
 *
 * The rows, the point of control, the value area, the single prints and the
 * letters are all decided in `packages/core` and served by `/v1/profile`. This
 * file places bars and paints them. Same reasoning as the footprint: the
 * plausible failure in a dense price picture is not a crash, it is a shape
 * that renders beautifully and is wrong, and arithmetic in a renderer is
 * arithmetic nobody tests.
 *
 * The one derived number here is each bar's WIDTH as a fraction of the busiest
 * row, which is a presentation choice rather than a market fact.
 *
 * ## The basis badge is not decoration
 *
 * A volume profile built from bars is a distribution, not a measurement: a bar
 * reports one figure for its whole range and the server spreads it evenly.
 * That flattens the shape in a predictable direction — the point of control
 * drifts toward the middle of wide bars. Rendering it identically to a profile
 * counted from the tape would be the same class of error as drawing delayed
 * data as real-time, so the badge says which one this is and never hides.
 */
import { useState } from 'react';
import type { JSX } from 'react';
import type { ProfileDto, ProfileRowDto } from '@aegis/api-client';
import { useProfile, type ProfilePeriod } from '../lib/profile.js';

const PERIODS: readonly { id: ProfilePeriod; label: string; title: string }[] = [
  { id: 'day', label: 'Day', title: 'The whole trading day, overnight included' },
  { id: 'session', label: 'Session', title: 'Regular hours only — the overnight is dropped, not folded in' },
  { id: 'week', label: 'Week', title: 'Trading days grouped from Monday' },
];

/** Volume profile bars, TPO letters, or both stacked. */
type Mode = 'volume' | 'tpo';

const MODES: readonly { id: Mode; label: string; title: string }[] = [
  { id: 'volume', label: 'Volume', title: 'How much traded at each price' },
  { id: 'tpo', label: 'TPO', title: 'Which periods traded at each price' },
];

export interface ProfilePanelProps {
  readonly symbol: string;
  /** Bars the profile is built from. Finer places volume more accurately. */
  readonly timeframe?: string;
  readonly onError?: (message: string) => void;
}

export function ProfilePanel({
  symbol, timeframe = '5m', onError,
}: ProfilePanelProps): JSX.Element {
  const [period, setPeriod] = useState<ProfilePeriod>('day');
  const [mode, setMode] = useState<Mode>('volume');
  const [selected, setSelected] = useState(0);

  const { data, loading } = useProfile({
    symbol, timeframe, period, ...(onError ? { onError } : {}),
  });

  const profiles = data?.profiles ?? [];
  // Clamped rather than reset. A trader who has scrolled to an older session
  // and changes the row grouping should stay where they were looking.
  const index = Math.min(selected, Math.max(0, profiles.length - 1));
  const profile = profiles[index];

  return (
    <div className="profile-panel">
      <div className="profile-controls">
        <div className="profile-seg" role="group" aria-label="Profile period">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`chip-btn compact ${period === p.id ? 'on' : ''}`}
              aria-pressed={period === p.id}
              title={p.title}
              onClick={() => { setPeriod(p.id); setSelected(0); }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="profile-seg" role="group" aria-label="What to show">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`chip-btn compact ${mode === m.id ? 'on' : ''}`}
              aria-pressed={mode === m.id}
              title={m.title}
              disabled={m.id === 'tpo' && profile !== undefined && !profile.tpoAvailable}
              onClick={() => setMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {profiles.length > 1 && (
        <div className="profile-windows" role="tablist" aria-label="Which window">
          {profiles.map((p, i) => (
            <button
              key={`${p.label}-${p.from}`}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`chip-btn compact ${i === index ? 'on' : ''}`}
              onClick={() => setSelected(i)}
            >
              {p.label || 'Range'}
            </button>
          ))}
        </div>
      )}

      {profile ? (
        <ProfileBody profile={profile} mode={mode} />
      ) : (
        <p className="dim profile-empty">
          {loading ? 'Building the profile…' : 'Nothing traded in this window.'}
        </p>
      )}

      {profile && (
        <ProfileFooter profile={profile} truncated={data?.truncated ?? false} />
      )}
    </div>
  );
}

/** Where the volume figures came from, said plainly rather than in a tooltip. */
function BasisNote({ basis }: { basis: ProfileDto['basis'] }): JSX.Element {
  return basis === 'prints' ? (
    <span className="badge badge-ok" title="Counted from the tape — every print in the row it happened in">
      COUNTED
    </span>
  ) : (
    <span
      className="badge badge-warn"
      title={
        'Distributed, not counted. A bar reports one volume figure for its whole '
        + 'range, so it is spread evenly across the rows it spanned. This flattens '
        + 'the shape — the point of control drifts toward the middle of wide bars.'
      }
    >
      DISTRIBUTED
    </span>
  );
}

function ProfileBody({ profile, mode }: { profile: ProfileDto; mode: Mode }): JSX.Element {
  const busiest = profile.rows.reduce(
    (n, r) => Math.max(n, mode === 'tpo' ? r.periods : r.volume), 0,
  );
  const single = new Set(profile.singlePrints);

  return (
    <ol className="profile-rows" aria-label={`Profile for ${profile.label || 'the range'}`}>
      {/* Descending: a price axis reads high at the top, and a profile drawn
          the other way up is a different picture of the same numbers. */}
      {[...profile.rows].reverse().map((row) => (
        <ProfileRow
          key={row.price}
          row={row}
          mode={mode}
          busiest={busiest}
          isPoc={row.price === profile.poc}
          inValue={
            profile.val !== null && profile.vah !== null
            && Number(row.price) >= Number(profile.val)
            && Number(row.price) <= Number(profile.vah)
          }
          isSingle={single.has(row.price)}
        />
      ))}
    </ol>
  );
}

function ProfileRow({
  row, mode, busiest, isPoc, inValue, isSingle,
}: {
  row: ProfileRowDto; mode: Mode; busiest: number;
  isPoc: boolean; inValue: boolean; isSingle: boolean;
}): JSX.Element {
  const amount = mode === 'tpo' ? row.periods : row.volume;
  // Zero-width for an untouched row rather than a hairline. A row nothing
  // traded in is the gap in the shape, and a minimum width would fill it in.
  const width = busiest > 0 ? (amount / busiest) * 100 : 0;

  const classes = [
    'profile-row',
    isPoc ? 'poc' : '',
    inValue ? 'in-value' : '',
    isSingle ? 'single' : '',
  ].filter(Boolean).join(' ');

  return (
    <li className={classes}>
      <span className="profile-price mono">{row.price}</span>
      <span className="profile-bar-track">
        <span className="profile-bar" style={{ inlineSize: `${width}%` }} />
        {mode === 'tpo' && row.tpo !== '' && (
          <span className="profile-tpo mono">{row.tpo}</span>
        )}
      </span>
      <span className="profile-amount mono">
        {mode === 'tpo' ? row.periods : Math.round(row.volume).toLocaleString('en-US')}
      </span>
    </li>
  );
}

function ProfileFooter({
  profile, truncated,
}: { profile: ProfileDto; truncated: boolean }): JSX.Element {
  return (
    <div className="profile-footer">
      <dl className="profile-stats">
        <div><dt>POC</dt><dd className="mono">{profile.poc ?? '—'}</dd></div>
        <div><dt>VAH</dt><dd className="mono">{profile.vah ?? '—'}</dd></div>
        <div><dt>VAL</dt><dd className="mono">{profile.val ?? '—'}</dd></div>
        <div><dt>Rows</dt><dd className="mono">{profile.rowSize}</dd></div>
      </dl>
      <div className="profile-notes">
        <BasisNote basis={profile.basis} />
        {!profile.tpoAvailable && profile.periodCount > 0 && (
          <span className="dim" title={
            `${profile.periodCount} periods is more than the alphabet has letters. `
            + 'Two periods sharing one would be unreadable, so none are shown. '
            + 'A longer period gives them back.'
          }>
            No letters — {profile.periodCount} periods
          </span>
        )}
        {truncated && (
          <span className="dim" title="Older windows exist and were not fetched.">
            Newest windows only
          </span>
        )}
      </div>
    </div>
  );
}
